import { useTranslation } from "react-i18next";

import { SectionCard } from "@/components/ui/section-card";
import { env } from "@/lib/env";

function Snippet({ label, code }: { label: string; code: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-ink-700">{label}</span>
      <pre className="overflow-x-auto rounded-lg border border-line bg-surface-muted p-3 text-xs leading-relaxed text-ink-800">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function DocsBlock({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-ink-950">{title}</h3>
      {children}
    </div>
  );
}

export function ServiceUserUsageDocs() {
  const { t } = useTranslation("serviceUsers");
  const base = env.API_BASE_URL;

  return (
    <SectionCard title={t("usage.title")} description={t("usage.description")}>
      <div className="flex flex-col gap-1 text-sm">
        <span className="text-xs font-medium text-ink-700">
          {t("usage.baseUrlLabel")}
        </span>
        <code className="rounded-lg border border-line bg-surface-muted px-3 py-2 text-xs text-ink-800">
          {base}
        </code>
      </div>

      <DocsBlock title={t("usage.authTitle")}>
        <p className="text-xs text-ink-500">{t("usage.authDescription")}</p>
        <code className="rounded-lg border border-line bg-surface-muted px-3 py-2 text-xs text-ink-800">
          Authorization: Bearer orbit_sk_...
        </code>
      </DocsBlock>

      <DocsBlock title={t("usage.agentsTitle")}>
        <p className="text-xs text-ink-500">{t("usage.sessionHint")}</p>
        <Snippet
          label={t("usage.listAgents")}
          code={`curl ${base}/v1/agents \\\n  -H "Authorization: Bearer orbit_sk_..."`}
        />
        <Snippet
          label={t("usage.invoke")}
          code={`curl -X POST ${base}/v1/agents/AGENT_ID_OR_SLUG/invoke \\\n  -H "Authorization: Bearer orbit_sk_..." \\\n  -H "Content-Type: application/json" \\\n  -d '{"session_id": "minha-sessao", "chatInput": "Olá"}'`}
        />
        <Snippet
          label={t("usage.stream")}
          code={`curl -N -X POST ${base}/v1/agents/AGENT_ID_OR_SLUG/invoke/stream \\\n  -H "Authorization: Bearer orbit_sk_..." \\\n  -H "Content-Type: application/json" \\\n  -d '{"session_id": "minha-sessao", "chatInput": "Olá"}'`}
        />
        <Snippet
          label={t("usage.cancel")}
          code={`curl -X POST ${base}/v1/agents/AGENT_ID_OR_SLUG/invoke/cancel \\\n  -H "Authorization: Bearer orbit_sk_..." \\\n  -H "Content-Type: application/json" \\\n  -d '{"session_id": "minha-sessao"}'`}
        />
        <p className="text-xs text-ink-500">{t("usage.syncVsStream")}</p>
        <p className="text-xs text-ink-500">{t("usage.hitlHint")}</p>
      </DocsBlock>

      <DocsBlock title={t("usage.tasksTitle")}>
        <p className="text-xs text-ink-500">{t("usage.tasksDescription")}</p>
        <Snippet
          label={t("usage.taskSingle")}
          code={`curl -X POST ${base}/v1/tasks \\\n  -H "Authorization: Bearer orbit_sk_..." \\\n  -H "Content-Type: application/json" \\\n  -d '{\n  "agent": "AGENT_ID_OR_SLUG",\n  "chatInput": "Gere o relatório semanal",\n  "callback_url": "https://meu-app/webhooks/orbit"\n}'`}
        />
        <Snippet
          label={t("usage.taskTeam")}
          code={`curl -X POST ${base}/v1/tasks \\\n  -H "Authorization: Bearer orbit_sk_..." \\\n  -H "Content-Type: application/json" \\\n  -d '{\n  "team": {\n    "agents": ["pesquisador", "redator"],\n    "supervisor": "coordenador",\n    "instructions": "Trabalhem em conjunto"\n  },\n  "chatInput": "Resuma as novidades",\n  "callback_url": "https://meu-app/webhooks/orbit"\n}'`}
        />
        <Snippet
          label={t("usage.taskList")}
          code={`curl ${base}/v1/tasks \\\n  -H "Authorization: Bearer orbit_sk_..."`}
        />
        <Snippet
          label={t("usage.taskGet")}
          code={`curl ${base}/v1/tasks/TASK_ID \\\n  -H "Authorization: Bearer orbit_sk_..."`}
        />
        <Snippet
          label={t("usage.taskCancel")}
          code={`curl -X POST ${base}/v1/tasks/TASK_ID/cancel \\\n  -H "Authorization: Bearer orbit_sk_..."`}
        />
      </DocsBlock>

      <DocsBlock title={t("usage.usageTitle")}>
        <p className="text-xs text-ink-500">{t("usage.usageDescription")}</p>
        <Snippet
          label={t("usage.usageEndpoint")}
          code={`curl ${base}/v1/usage \\\n  -H "Authorization: Bearer orbit_sk_..."`}
        />
      </DocsBlock>

      <DocsBlock title={t("usage.webhooksTitle")}>
        <p className="text-xs text-ink-500">{t("usage.webhooksDescription")}</p>
        <code className="rounded-lg border border-line bg-surface-muted px-3 py-2 text-xs text-ink-800">
          X-Orbit-Signature: sha256=...
        </code>
      </DocsBlock>
    </SectionCard>
  );
}
